import { create } from 'zustand'

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated'
export type ProfileStatus = 'idle' | 'loading' | 'ready' | 'error'
export type Workspace = 'admin' | 'trainer'

type SetProfileOptions = {
  persist?: boolean
  updatedAt?: number
}

export const AUTH_PROFILE_STORAGE_KEY = 'gym.auth.profile'
export const AUTH_PROFILE_META_STORAGE_KEY = 'gym.auth.profile.meta'
export const AUTH_WORKSPACE_STORAGE_KEY = 'gym.auth.workspace'

function readStoredWorkspace(): Workspace | null {
  if (typeof window === 'undefined') return null

  const storedWorkspace = localStorage.getItem(AUTH_WORKSPACE_STORAGE_KEY)
  if (storedWorkspace === 'admin' || storedWorkspace === 'trainer') {
    return storedWorkspace
  }

  return null
}

export interface Profile {
  id: string
  email: string
  emailVerified?: boolean
  role: 'member' | 'trainer' | 'admin' | 'super_admin' | 'system'
  hasTrainerProfile: boolean
  firstName: string
  lastName: string
  phone?: string | null

  // Datos personales adicionales
  rut?: string | null
  documentType?: 'rut' | 'passport'
  documentNumber?: string | null
  dateOfBirth?: string | null
  gender?: 'male' | 'female' | 'other' | 'prefer_not_to_say' | null

  // Métricas físicas
  health?: {
    weight?: number | null // en gramos (backend)
    height?: number | null // en centímetros
    imc?: number | null // IMC x100 (backend)
    musclePercentage?: number | null
    fatPercentage?: number | null
    muscleWeight?: number | null // en gramos
    fatWeight?: number | null // en gramos
    rightArmDiameter?: number | null // en centímetros
    leftArmDiameter?: number | null // en centímetros
    rightThighDiameter?: number | null // en centímetros
    leftThighDiameter?: number | null // en centímetros
    neckDiameter?: number | null // en centímetros
  } | null
}

function normalizeProfile(profile: Profile | null): Profile | null {
  if (!profile) return null

  return {
    ...profile,
    hasTrainerProfile: Boolean(profile.hasTrainerProfile),
  }
}

function resolveWorkspace(
  profile: Profile | null,
  preferredWorkspace: Workspace | null
): Workspace | null {
  if (!profile) return null

  if (profile.role === 'trainer') {
    return 'trainer'
  }

  if (profile.role === 'admin' || profile.role === 'super_admin') {
    if (profile.hasTrainerProfile) {
      return preferredWorkspace === 'trainer' ? 'trainer' : 'admin'
    }

    return 'admin'
  }

  return null
}

function persistWorkspace(workspace: Workspace | null) {
  if (typeof window === 'undefined') return

  if (workspace) {
    localStorage.setItem(AUTH_WORKSPACE_STORAGE_KEY, workspace)
    return
  }

  localStorage.removeItem(AUTH_WORKSPACE_STORAGE_KEY)
}

interface AuthState {
  session: { source: 'cookie' } | null
  profile: Profile | null
  status: AuthStatus
  profileStatus: ProfileStatus
  profileUpdatedAt: number | null
  activeWorkspace: Workspace | null
  setAuth: (session: { source: 'cookie' } | null) => void
  setProfile: (profile: Profile | null, options?: SetProfileOptions) => void
  setProfileStatus: (status: ProfileStatus) => void
  setProfileUpdatedAt: (value: number | null) => void
  setStatus: (status: AuthStatus) => void
  setActiveWorkspace: (workspace: Workspace) => void
  getExpectedWorkspaceForPath: (pathname: string) => Workspace | null
  reset: () => void

  // Role helpers
  getRole: () => Profile['role'] | null
  getHomePath: () => string
  isAdmin: () => boolean
  isAdminOrSuperAdmin: () => boolean
  isSuperAdmin: () => boolean
  isTrainer: () => boolean
  hasTrainerProfile: () => boolean
  canAccessTrainerWorkspace: () => boolean
  isMember: () => boolean
  hasRole: (role: Profile['role'] | Profile['role'][]) => boolean
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  session: null,
  profile: null,
  status: 'loading',
  profileStatus: 'idle',
  profileUpdatedAt: null,
  activeWorkspace: readStoredWorkspace(),
  setAuth: (session) => {
    set(() => ({
      session,
      status: session ? 'authenticated' : 'unauthenticated',
    }))
  },
  setStatus: (status) => {
    set(() => ({ status }))
  },
  setProfile: (profile, options = {}) => {
    const normalizedProfile = normalizeProfile(profile)
    const updatedAt = normalizedProfile
      ? (options.updatedAt ?? Date.now())
      : null

    if (typeof window !== 'undefined' && options.persist !== false) {
      if (normalizedProfile) {
        localStorage.setItem(
          AUTH_PROFILE_STORAGE_KEY,
          JSON.stringify(normalizedProfile)
        )
        localStorage.setItem(
          AUTH_PROFILE_META_STORAGE_KEY,
          JSON.stringify({ updatedAt })
        )
      } else {
        localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY)
        localStorage.removeItem(AUTH_PROFILE_META_STORAGE_KEY)
      }
    }

    const nextWorkspace = resolveWorkspace(
      normalizedProfile,
      get().activeWorkspace ?? readStoredWorkspace()
    )
    persistWorkspace(nextWorkspace)

    set(() => ({
      profile: normalizedProfile,
      profileUpdatedAt: updatedAt,
      activeWorkspace: nextWorkspace,
    }))
  },
  setProfileStatus: (profileStatus) =>
    set(() => ({
      profileStatus,
    })),
  setProfileUpdatedAt: (profileUpdatedAt) =>
    set(() => ({
      profileUpdatedAt,
    })),
  setActiveWorkspace: (workspace) => {
    const nextWorkspace = resolveWorkspace(get().profile, workspace)
    persistWorkspace(nextWorkspace)
    set(() => ({
      activeWorkspace: nextWorkspace,
    }))
  },
  getExpectedWorkspaceForPath: (pathname) => {
    const profile = get().profile
    if (!profile) return null

    if (profile.role === 'trainer') {
      return 'trainer'
    }

    const isAdminOrSuperAdmin =
      profile.role === 'admin' || profile.role === 'super_admin'

    if (!isAdminOrSuperAdmin) {
      return null
    }

    if (!profile.hasTrainerProfile) {
      return 'admin'
    }

    const isTrainerWorkspaceRoute =
      pathname === '/trainer' || pathname.startsWith('/trainer/')
    if (isTrainerWorkspaceRoute) {
      return 'trainer'
    }

    const isTrainerSettingsRoute =
      pathname === '/settings/trainer-profile' ||
      pathname.startsWith('/settings/trainer-profile/')
    if (isTrainerSettingsRoute) {
      return 'trainer'
    }

    const isSharedSettingsRoute =
      pathname === '/settings' ||
      pathname.startsWith('/settings/account') ||
      pathname.startsWith('/settings/appearance') ||
      pathname.startsWith('/settings/display') ||
      pathname.startsWith('/settings/notifications')

    if (isSharedSettingsRoute) {
      return get().activeWorkspace === 'trainer' ? 'trainer' : 'admin'
    }

    return 'admin'
  },
  reset: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(AUTH_PROFILE_STORAGE_KEY)
      localStorage.removeItem(AUTH_PROFILE_META_STORAGE_KEY)
      localStorage.removeItem(AUTH_WORKSPACE_STORAGE_KEY)
    }

    set(() => ({
      session: null,
      profile: null,
      status: 'unauthenticated',
      profileStatus: 'idle',
      profileUpdatedAt: null,
      activeWorkspace: null,
    }))
  },
  // Role helpers
  getRole: () => get().profile?.role ?? null,
  getHomePath: () => {
    const profile = get().profile
    if (!profile) return '/dashboard'

    if (
      get().activeWorkspace === 'trainer' &&
      (profile.role === 'trainer' ||
        ((profile.role === 'admin' || profile.role === 'super_admin') &&
          profile.hasTrainerProfile))
    ) {
      return '/trainer'
    }

    switch (profile.role) {
      case 'member':
        return '/member-home'
      case 'trainer':
        return '/trainer'
      case 'admin':
        return '/admin-home'
      case 'super_admin':
        return '/dashboard'
      case 'system':
        return '/system/check-ins'
      default:
        return '/dashboard'
    }
  },
  isAdmin: () => {
    const role = get().profile?.role
    return role === 'admin'
  },
  isAdminOrSuperAdmin: () => {
    const role = get().profile?.role
    return role === 'admin' || role === 'super_admin'
  },
  isSuperAdmin: () => get().profile?.role === 'super_admin',
  isTrainer: () => {
    const role = get().profile?.role
    return role === 'trainer'
  },
  hasTrainerProfile: () => Boolean(get().profile?.hasTrainerProfile),
  canAccessTrainerWorkspace: () => {
    const profile = get().profile
    if (!profile) return false

    if (profile.role === 'trainer') return true

    return (
      (profile.role === 'admin' || profile.role === 'super_admin') &&
      profile.hasTrainerProfile
    )
  },
  isMember: () => get().profile?.role === 'member',
  hasRole: (roles) => {
    const role = get().profile?.role
    if (!role) return false
    return Array.isArray(roles) ? roles.includes(role) : role === roles
  },
}))
